import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import PreviewImage from '../PreviewImage';
import styles from './GalleryIntro.module.sass';

// The beat sequence:
//   0.3s  the name block fades up on black — "Class of 2027 / Chloe / your portraits
//         are ready". Nothing else is on screen yet.
//   0.9s  photos arrive ONE AT A TIME, down the left column then down the right:
//         top-left, bottom-left (overlapping it), top-right, bottom-right. Each one
//         fades in LOW and SMALL, then grows and rises into its slot — the travel is
//         the effect, so the entrance is slow (1.2s) and the gap between photos (900ms)
//         is long enough to read as "one, pause, the next".
//   4.8s  the last photo has landed; they hold.
//   5.6s  photos blur back and the "View your gallery" button resolves under the name.
//
// Slot ORDER is the reveal order, so .slot0…3 must stay TL, BL, TR, BR (see the sass).
const PHOTO_SLOT_DELAYS = [900, 1800, 2700, 3600];
const PHOTO_SLOT_COUNT = PHOTO_SLOT_DELAYS.length;

// From the moment the photos APPEAR (not from mount) to the blur + CTA beat: the last
// photo starts at 3.6s, takes 1.2s to land, then holds.
const SEQUENCE_MS = 5600;

// How long to wait for the hero images to decode before showing them anyway.
const DECODE_WAIT_MS = 4000;

// If no photo ever arrives, unlock the CTA regardless — never trap the parent.
const NO_PHOTOS_FALLBACK_MS = 9000;

// Choose up to 4 hero photos out of the student's session. Prefers the
// caller-supplied list; falls back to first, last, and a couple in between
// so we always get spread across the shoot rather than 4 near-duplicates.
const pickHeroPhotos = (heroPhotos, allPhotos) => {
  if (heroPhotos && heroPhotos.length > 0) return heroPhotos.slice(0, 4);
  const src = allPhotos || [];
  if (src.length <= 4) return src.slice(0);
  const first = src[0];
  const last = src[src.length - 1];
  const q1 = src[Math.floor(src.length / 4)];
  const q3 = src[Math.floor((src.length * 3) / 4)];
  return [first, q1, q3, last];
};

export default function GalleryIntro({
  studentFirstName,
  eyebrow,
  heroPhotos,
  allPhotos,
  onComplete,
  preloadUrls,
}) {
  // TWO separate states, deliberately. `showFinal` reveals the CTA; `dimmed` blurs the
  // photos. Coupling them meant a slow pose load blurred the photos the moment the CTA
  // appeared — so they arrived on screen already blurred, which is not a thing that should
  // be possible no matter how slow the network is. Photos are dimmed only once they have
  // actually had their run.
  const [showFinal, setShowFinal] = useState(false);
  const [dimmed, setDimmed] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // LATCHED, and only once the images are DECODED.
  //
  // The intro deliberately opens before the poses have loaded, so allPhotos arrives late.
  // Two things follow, and both used to be wrong:
  //
  //   1. The photo beats must start when the photos EXIST, not when the intro mounts.
  //      Timing the sequence from mount meant that on a slow load the "blur back and show
  //      the CTA" beat had already fired before the photos arrived — so they appeared
  //      already blurred, in the wrong order.
  //   2. Latch a photo only after its image has decoded, so it pops in crisp instead of
  //      rendering a half-streamed image and sharpening up on screen.
  //
  // Once latched they never change: a later refetch must not disturb a running intro.
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (photos.length > 0) return undefined;
    const picked = pickHeroPhotos(heroPhotos, allPhotos).slice(0, PHOTO_SLOT_COUNT);
    if (picked.length === 0) return undefined;

    let cancelled = false;
    const reveal = () => { if (!cancelled) setPhotos(picked); };

    const urls = picked
      .flatMap((photo) => [photo?.foregroundUrl, photo?.backgroundUrl])
      .filter(Boolean);

    const loads = urls.map((url) => new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve; // a broken image must not stall the intro
      img.src = url;
    }));

    // Never wait on the network longer than this: a slow image degrades to popping in as
    // it arrives, which is better than a parent staring at a name on a black screen.
    const guard = setTimeout(reveal, DECODE_WAIT_MS);
    Promise.all(loads).then(() => { clearTimeout(guard); reveal(); });

    return () => { cancelled = true; clearTimeout(guard); };
  }, [heroPhotos, allPhotos, photos.length]);

  // Prefers-reduced-motion: skip the movement, land straight on the final beat.
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // The blur + CTA beat, timed from the moment the PHOTOS appear — not from mount.
  useEffect(() => {
    if (reducedMotion) {
      setShowFinal(true);
      setDimmed(true);
      return undefined;
    }
    if (photos.length === 0) return undefined;
    const t = setTimeout(() => {
      setShowFinal(true);
      setDimmed(true);
    }, SEQUENCE_MS);
    return () => clearTimeout(t);
  }, [reducedMotion, photos.length]);

  // Backstop: if the photos are slow or never arrive, the parent still gets a way forward
  // rather than a name on a black screen. It reveals the CTA ONLY — it never dims, so
  // photos that turn up afterwards still play their entrance, crisp.
  useEffect(() => {
    if (showFinal) return undefined;
    const t = setTimeout(() => setShowFinal(true), NO_PHOTOS_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [showFinal]);

  // Warm the gallery underneath while the intro plays, so it renders from cache.
  useEffect(() => {
    if (!preloadUrls || preloadUrls.length === 0) return;
    preloadUrls.forEach((url) => { new Image().src = url; });
  }, [preloadUrls]);

  const finish = () => {
    if (dismissing) return;
    setDismissing(true);
    // Short fade-out before calling onComplete so the transition into the
    // gallery feels like a resolve, not a hard cut. Match the CSS duration.
    setTimeout(() => onComplete && onComplete(), 260);
  };

  return (
    <div
      className={cn(styles.overlay, { [styles.overlayDismissing]: dismissing })}
      role="dialog"
      aria-modal="true"
      aria-label="Gallery intro"
    >
      <button type="button" className={styles.skip} onClick={finish}>
        Skip
        <span className={styles.skipArrow} aria-hidden="true">›</span>
      </button>

      <div className={styles.stage}>
        {photos.map((photo, i) => (
          <div
            key={photo?.foregroundUrl || i}
            className={cn(styles.photo, styles[`slot${i}`], { [styles.photoDimmed]: dimmed })}
            style={{ animationDelay: `${PHOTO_SLOT_DELAYS[i]}ms` }}
          >
            <div className={styles.photoInner}>
              {photo?.foregroundUrl ? (
                <PreviewImage
                  foregroundUrl={photo.foregroundUrl}
                  backgroundUrl={photo.backgroundUrl}
                />
              ) : (
                <span className={styles.photoPlaceholder} aria-hidden="true">👤</span>
              )}
            </div>
          </div>
        ))}

        {/* ONE name block for the whole intro — it is the first thing on screen and it
            never moves. The photos arrive around it and then blur back behind it; only
            the CTA is added at the end. Swapping in a second, differently-styled block
            at the final beat made the text jump. */}
        <div className={styles.nameBlock}>
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <p className={styles.name}>{studentFirstName || 'Your'}</p>
          <div className={styles.accentLine} aria-hidden="true" />
          <p className={styles.subline}>Your portraits are ready</p>

          {showFinal && (
            <button type="button" className={styles.finalCta} onClick={finish}>
              View your gallery
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

GalleryIntro.defaultProps = {
  studentFirstName: '',
  eyebrow: '',
  heroPhotos: null,
  allPhotos: null,
  preloadUrls: null,
};

// Each photo: { foregroundUrl, backgroundUrl } — matches how the gallery
// composites poses (unwatermarked foreground on a chosen backdrop). No
// actualForegroundUrl on purpose: we skip the "Class of YYYY" graphics
// overlay in the intro since the class year already renders in the text.
const photoShape = PropTypes.shape({
  foregroundUrl: PropTypes.string,
  backgroundUrl: PropTypes.string,
});

GalleryIntro.propTypes = {
  studentFirstName: PropTypes.string,
  // The line above the name. Seniors pass "Class of 2027"; every other audience passes
  // the student's grade ("11th Grade"). Falsy => the line is omitted entirely, which is
  // what gradeLabel() returns for staff / unknown grades.
  eyebrow: PropTypes.string,
  heroPhotos: PropTypes.arrayOf(photoShape),
  allPhotos: PropTypes.arrayOf(photoShape),
  preloadUrls: PropTypes.arrayOf(PropTypes.string),
  onComplete: PropTypes.func.isRequired,
};
